import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fmtUSD } from "../../utils/formatters";
import { showToast } from "../../store/uiSlice";
import { adminApi } from "../../services/api";

const UserDetailModal = ({ user, leadInfo, onClose }) => {
  const dispatch = useDispatch();
  const { adminUser } = useSelector((state) => state.admin);

  // ─── FREJUN DIALER STATE ───
  const [showDialer, setShowDialer] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [dialerReady, setDialerReady] = useState(false);
  const dialerIframeRef = useRef(null);
  const pendingCallRef = useRef(null);

  const FREJUN_TOKEN = import.meta.env.VITE_FREJUN_TOKEN;
  const FREJUN_EMAIL = import.meta.env.VITE_FREJUN_EMAIL || "finance@groxion.com";

  // Strict masking logic
  const canSeeFullDetails = () => {
    if (!adminUser) return false;
    return adminUser.role === "admin" || adminUser.role === "super_admin";
  };

  // Helper to check if a lead belongs to the new retention table
  const isRetentionData = (lead) => {
    if (!lead) return false;
    // Check if it's from the retention table (source is Brand Name or short_id starts with RET)
    return lead.short_id?.startsWith("RET") || lead.source === "RetentionData_Upload" || lead.source === "Quantum" || lead.source === "naka_";
  };

  // ─── FREJUN DIALER LISTENER ───
  useEffect(() => {
    const handleDialerMessage = (event) => {
      if (event.origin !== "https://dialer.frejun.com") return;
      const { eventName, detail } = event.data || {};

      if (eventName === "ready") {
        setDialerReady(true);
        dialerIframeRef.current?.contentWindow.postMessage({
          eventName: "authorize",
          data: { access_token: FREJUN_TOKEN, user_email: FREJUN_EMAIL }
        }, "https://dialer.frejun.com");
      }

      if (eventName === "unauthorized") {
        setDialerReady(false);
        setIsCallActive(false);
        dispatch(showToast({ message: "Dialer auth failed. Token expired.", type: "error" }));
      }
      
      if (eventName === "close" || eventName === "call-ended" || eventName === "end-call-success") {
        setIsCallActive(false);
      }

      if (eventName === "end-call-error") {
        setIsCallActive(false);
      }
    };
    window.addEventListener("message", handleDialerMessage);
    return () => window.removeEventListener("message", handleDialerMessage);
  }, [dispatch, FREJUN_TOKEN, FREJUN_EMAIL]);

  const handleFrejunCall = (phoneNumber, leadName) => {
    if (!phoneNumber) return;
    setIsCallActive(true);

    if (!showDialer) {
      setShowDialer(true);
      pendingCallRef.current = { phoneNumber, leadName };
      return;
    }

    if (dialerReady && dialerIframeRef.current) {
      dialerIframeRef.current.contentWindow.postMessage({
        eventName: "initiate-call",
        data: {
          user_email: FREJUN_EMAIL,
          candidate_number: phoneNumber,
          candidate_name: leadName || "",
          access_token: FREJUN_TOKEN,
        }
      }, "https://dialer.frejun.com");
    } else {
      pendingCallRef.current = { phoneNumber, leadName };
    }
  };

  useEffect(() => {
    if (dialerReady && pendingCallRef.current && dialerIframeRef.current) {
      const { phoneNumber, leadName } = pendingCallRef.current;
      dialerIframeRef.current.contentWindow.postMessage({
        eventName: "initiate-call",
        data: {
          user_email: FREJUN_EMAIL,
          candidate_number: phoneNumber,
          candidate_name: leadName || "",
          access_token: FREJUN_TOKEN,
        }
      }, "https://dialer.frejun.com");
      pendingCallRef.current = null;
    }
  }, [dialerReady, FREJUN_TOKEN, FREJUN_EMAIL]);

  const handleHangup = () => {
    if (dialerIframeRef.current) {
      dialerIframeRef.current.contentWindow.postMessage(
        { eventName: "end-call", data: {} },
        "https://dialer.frejun.com"
      );
    }
    setIsCallActive(false);
  };

  // Triggers FreJun Dialer securely
  const handleDialNumber = async (e) => {
    e.stopPropagation();
    try {
      // We use the leadId to get the phone number securely
      if (!leadInfo || !leadInfo.id) {
        dispatch(showToast({ message: "No lead linked to this user", type: "error" }));
        return;
      }

      // Route to the correct endpoint based on the lead source
      const endpoint = isRetentionData(leadInfo)
        ? `/leads/retentiondata/${leadInfo.id}/dial`
        : `/leads/${leadInfo.id}/dial`;

      const res = await adminApi.get(endpoint);
      
      if (res.data?.phone) {
        handleFrejunCall(res.data.phone, user.firstName + " " + user.lastName);
      }
    } catch (e) {
      dispatch(showToast({ message: "No phone number found", type: "error" }));
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="relative bg-white border-2 border-[#e2e8f0] rounded-2xl w-full max-w-[650px] max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white px-6 py-4 border-b-2 border-[#e2e8f0] flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0b1e33] to-[#1e3a5f] flex items-center justify-center shadow-md shadow-[#1e3a5f]/25">
              <span className="text-sm font-extrabold text-white">{user.firstName?.[0]}{user.lastName?.[0]}</span>
            </div>
            <div>
              <h3 className="font-syne text-lg font-extrabold text-[#0b1e33] flex items-center gap-2 uppercase tracking-tight">
                {user.firstName} {user.lastName}
                {leadInfo && <span className="text-[10px] font-mono font-extrabold text-[#1e3a5f] bg-[#eff6ff] border-2 border-[#bfdbfe] px-2 py-0.5 rounded-lg">#{leadInfo.short_id}</span>}
              </h3>
              <p className="text-[10px] text-[#475569] font-mono font-bold">USER ID: {user.id}</p>
            </div>
          </div>
          <button onClick={onClose} 
            className="w-9 h-9 rounded-lg bg-[#f8fafc] border-2 border-[#e2e8f0] text-[#64748b] hover:border-[#dc2626] hover:text-[#dc2626] hover:bg-[#fef2f2] transition-all flex items-center justify-center">
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Lead Information */}
          {leadInfo && (
            <div className="p-4 rounded-xl bg-[#eff6ff] border-2 border-[#bfdbfe]">
              <h4 className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <i className="fas fa-user-plus" /> LEAD INFORMATION
              </h4>
              <div className="grid grid-cols-2 gap-3 text-[11px]">
                <div>
                  <span className="text-[#64748b] font-mono font-bold">LEAD ID:</span>
                  <span className="text-[#0f172a] font-mono font-extrabold ml-2">#{leadInfo.short_id}</span>
                </div>
                <div>
                  <span className="text-[#64748b] font-mono font-bold">SOURCE:</span>
                  <span className="text-[#0f172a] font-extrabold ml-2">{leadInfo.source || 'WEBSITE'}</span>
                </div>
                <div>
                  <span className="text-[#64748b] font-mono font-bold">STATUS:</span>
                  <span className="text-[#0f172a] font-extrabold ml-2 uppercase">{leadInfo.status?.replace(/_/g, ' ') || '—'}</span>
                </div>
                <div>
                  <span className="text-[#64748b] font-mono font-bold">DEPOSIT:</span>
                  <span className={`ml-2 font-extrabold ${leadInfo.depositStatus === 'deposited' ? 'text-[#059669]' : 'text-[#64748b]'}`}>
                    {leadInfo.depositStatus === 'deposited' ? 'YES' : 'NO'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Personal Information */}
          <div>
            <h4 className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <i className="fas fa-user" /> PERSONAL INFORMATION
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">FULL NAME</div>
                <div className="text-[12px] text-[#0f172a] font-bold">{user.firstName} {user.lastName}</div>
              </div>
              <div>
                <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">EMAIL ADDRESS</div>
                <div className="text-[12px] text-[#0f172a] font-bold">
                  {canSeeFullDetails() ? user.email : "****@****"}
                </div>
              </div>
              <div>
                <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">PHONE NUMBER</div>
                <div className="text-[12px] text-[#0f172a] font-bold">
                  {canSeeFullDetails() ? (
                    user.phone || '—'
                  ) : (
                    user.phone ? (
                      <button onClick={handleDialNumber} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-[#ecfdf5] text-[#059669] border border-[#a7f3d0] hover:bg-[#d1fae5] transition-all font-extrabold text-[11px]">
                        <i className="fas fa-phone-alt text-[9px]" /> CALL
                      </button>
                    ) : '—'
                  )}
                </div>
              </div>
              <div>
                <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">DATE OF BIRTH</div>
                <div className="text-[12px] text-[#0f172a] font-bold">{user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}</div>
              </div>
              <div>
                <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">COUNTRY</div>
                <div className="text-[12px] text-[#0f172a] font-bold">{user.country || '—'}</div>
              </div>
              <div>
                <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">CITY</div>
                <div className="text-[12px] text-[#0f172a] font-bold">{user.city || '—'}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">ADDRESS</div>
                <div className="text-[12px] text-[#0f172a] font-bold">{user.address || '—'}</div>
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="pt-4 border-t-2 border-[#e2e8f0]">
            <h4 className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <i className="fas fa-wallet" /> ACCOUNT INFORMATION
            </h4>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'BALANCE', value: fmtUSD(user.balance || 0), color: '#059669' },
                { label: 'TOTAL DEPOSITS', value: fmtUSD(user.totalDeposits || 0), color: '#1e3a5f' },
                { label: 'TOTAL WITHDRAWALS', value: fmtUSD(user.totalWithdrawals || 0), color: '#dc2626' },
                { label: 'TOTAL TRADES', value: user.totalTrades || 0, color: '#2563eb', isNumber: true },
                { label: 'TOTAL P&L', value: `${(user.totalPnl || 0) >= 0 ? '+' : ''}${fmtUSD(user.totalPnl || 0)}`, color: (user.totalPnl || 0) >= 0 ? '#059669' : '#dc2626' },
                { label: 'WIN RATE', value: `${user.winRate || 0}%`, color: '#d97706' },
                { label: 'LEVERAGE', value: user.leverage || '1:100', color: '#0f172a' },
                { label: 'TAG', value: (user.tag || 'bronze').toUpperCase(), color: '#d97706' },
              ].map(item => (
                <div key={item.label}>
                  <div className="text-[9px] text-[#64748b] font-mono font-extrabold uppercase tracking-wider mb-1">{item.label}</div>
                  <div className="text-lg font-extrabold" style={{ color: item.color }}>
                    {item.isNumber ? item.value.toLocaleString() : item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Verification Status */}
          <div className="pt-4 border-t-2 border-[#e2e8f0]">
            <h4 className="text-[10px] text-[#1e3a5f] font-mono font-extrabold uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <i className="fas fa-shield-alt" /> VERIFICATION STATUS
            </h4>
            <div className="flex flex-wrap gap-4">
              {[
                { label: 'GOVERNMENT ID', verified: user.idVerified },
                { label: 'PROOF OF ADDRESS', verified: user.addressVerified },
                { label: 'SELFIE WITH ID', verified: user.selfieVerified },
                { label: 'ACCOUNT STATUS', verified: !user.isSuspended, trueLabel: 'ACTIVE', falseLabel: 'SUSPENDED' },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-2 bg-[#f8fafc] border-2 border-[#e2e8f0] rounded-lg px-3 py-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${item.verified ? 'bg-[#059669]' : 'bg-[#dc2626]'}`} />
                  <span className="text-[11px] text-[#0f172a] font-bold">{item.label}</span>
                  <span className={`text-[10px] font-mono font-extrabold uppercase tracking-wider ${item.verified ? 'text-[#059669]' : 'text-[#dc2626]'}`}>
                    {item.verified ? (item.trueLabel || 'VERIFIED') : (item.falseLabel || 'PENDING')}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Timestamps */}
          <div className="pt-4 border-t-2 border-[#e2e8f0] flex justify-between text-[10px] text-[#475569] font-mono font-bold">
            <span>JOINED: {user.createdAt ? new Date(user.createdAt).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
            <span>LAST ACTIVE: {user.lastActive ? new Date(user.lastActive).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-[#f8fafc] px-6 py-4 border-t-2 border-[#e2e8f0] flex gap-3">
          <button className="flex-1 py-3 rounded-xl bg-[#059669] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider transition-all hover:bg-[#047857] border-b-[3px] border-[#047857]">
            <i className="fas fa-check-circle mr-1.5" /> APPROVE KYC
          </button>
          <button className="flex-1 py-3 rounded-xl bg-[#dc2626] text-white text-[10px] font-syne font-extrabold uppercase tracking-wider transition-all hover:bg-[#b91c1c] border-b-[3px] border-[#b91c1c]">
            <i className="fas fa-ban mr-1.5" /> SUSPEND USER
          </button>
        </div>
      </div>

      {/* FREJUN DIALER (HIDDEN) */}
      {showDialer && (
        <div style={{ display: "none" }}>
          <iframe ref={dialerIframeRef} src="https://dialer.frejun.com/" allow="microphone" />
        </div>
      )}

      {/* CUSTOM END CALL BUTTON */}
      {isCallActive && (
        <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}>
          <button
            onClick={handleHangup}
            className="px-6 py-3 rounded-full bg-[#dc2626] text-white font-syne font-extrabold uppercase tracking-wider shadow-2xl hover:bg-[#b91c1c] transition-all border-b-[3px] border-[#b91c1c] flex items-center gap-2"
          >
            <i className="fas fa-phone-slash text-lg" /> END CALL
          </button>
        </div>
      )}
    </div>
  );
};

export default UserDetailModal;